/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import { observable, action, makeObservable, computed, runInAction } from 'mobx'
import { compareVersions } from 'compare-versions'
import { getIntl } from 'umi'
import { Modal } from '@/components'
import { WebRTC2 } from '@/utils/NIM/index'
import { meeting, user } from '@/models/index'
import { deviceData, satisfiesBrowser, ua } from '@/utils/deviceCheck'
import { DEVICE_TYPE } from '@/types/meeting'
import * as api from '../api'
import models from './index'
import { elkLog, elkReport } from '@/utils/elkReport'
import { setSentryUser } from '@/utils/sentry'

const avleVersions: Record<string, string> = {
  [DEVICE_TYPE.IPHONE]: '3.29.0',
  [DEVICE_TYPE.MAC]: '3.29.0',
  [DEVICE_TYPE.PC]: '3.29.0',
  [DEVICE_TYPE.ANDROID]: '3.29.0',
}

type deviceOption = {
  label: string
  value: string
}
type deviceOptions = {
  audioIn: deviceOption[]
  audioOut: deviceOption[]
  videoList: deviceOption[]
}

export enum NetworkStatus {
  UNKNOWN = 0, // 未知
  EXCELLENT = 1, // 优秀
  GOOD = 2, // 良好
  POOR = 3, // 较好
  BAD = 4, // 不好
  VERYBAD = 5, // 非常差
  DOWN = 6, // 网络异常
}
enum audioInTypes {
  loudspeaker,
  treceiver,
  headset,
  bluetooth,
}

async function refreshDeviceList(deviceObj?: any | undefined) {
  try {
    const dObj = deviceObj || { video: true, audio: true }
    const mediaStream = await navigator.mediaDevices.getUserMedia(dObj)
    if (dObj.video) {
      const videoTrack = mediaStream.getVideoTracks()[0]
      videoTrack?.stop()
    }
    if (dObj.audio) {
      const audioTrack = mediaStream.getAudioTracks()[0]
      audioTrack?.stop()
    }
  } catch (err) {
    console.warn('refreshDeviceList-err', err)
  }
}

function dealDiviceErr(err: any) {
  const { formatMessage } = getIntl()
  if (err.toString().match(/Permission denied|NotAllowedError|NOT_ALLOWED 41014/g)) {
    let osName = 'windows'
    const regMap: Record<string, RegExp> = {
      WINDOWS: /windows/i,
      MAC: /mac/i,
      IOS: /ios/i,
      ANDROID: /ANDROID/i,
    }
    Object.keys(regMap).some(key => {
      if (ua?.os?.name?.match?.(regMap[key])) {
        osName = key
        return true
      }
      return false
    })
    Modal.info({
      title: formatMessage({ id: 'DIVIECE_DISABLE_TITLE' }),
      content: formatMessage({ id: `DIVIECE_DISABLE_${osName.toUpperCase()}` }),
      okText: formatMessage({ id: 'CONFIRM' }),
      okButtonProps: {
        className: `popo-meeting-button`,
        danger: true,
      },
    })
  }
}

const unSubTimerMap: Record<string | number, number> = {}

class RTCInstance {
  constructor() {
    makeObservable(this)
  }

  @observable client: any = null

  // 本地流
  @observable localStream: any = null

  // 远端流
  @observable remoteStreamsMap: Map<number, any> = new Map()

  @observable remoteVideoMap: { [p: string]: boolean } = {}

  @observable remoteScreenMap: { [p: string]: boolean } = {}

  @observable isOpenVideo = false

  @observable isOpenAudio = false

  @observable muteMap = new Map()

  @observable isOpenAudioLoading = false

  @observable isShareScreen = false

  @observable micIsNotAvailable = false

  @observable cameraIsNotAvailable = false

  @observable isPublish = false

  @observable demoMap: any = new Map()

  @observable randomUid: number = Math.floor(Math.random() * 100000)

  @observable localCameraId = window.sessionStorage.getItem('cameraId') || ''

  @observable isBackCamera = false

  @observable joined = false

  @observable cid = 0

  @observable deviceOptions: deviceOptions = {
    audioIn: [],
    audioOut: [],
    videoList: [],
  }

  @observable videoValue = window.sessionStorage.getItem('cameraId') || ''

  @observable audioInValue = window.sessionStorage.getItem('micId') || ''

  @observable audioOutValue = ''

  @observable audioInType = audioInTypes.loudspeaker

  @observable networkQuality = new Map<string, boolean>()
  @observable networkQualityMap = new Map<
    string,
    { downlinkNetworkQuality: NetworkStatus; uplinkNetworkQuality: NetworkStatus }
  >()

  @observable audioIsPlaying = false

  @observable audioPlayErr = false

  @observable disconnected = false

  @observable connecting = false

  @observable firstConnecting = true

  @observable reconnected = false

  @observable curState = ''
  // 音视频是否异常
  @observable rtcTrackEnded = false

  // @observable micId: string = '';
  // @observable cameraId: string = '';

  static checkMicDeviceInList = async () => {
    let micId = window.sessionStorage.getItem('micId') || ''
    try {
      const mics = await WebRTC2.getMicrophones()
      const recommend = mics?.find((n: any) => !n.label?.match(/virtual|虚拟/i)) || mics?.[0]
      if (micId) {
        if (mics) {
          let micIdIsMatch = false
          for (let i = 0; i < mics.length; i++) {
            if (mics[i].deviceId === micId) {
              micIdIsMatch = true
              break
            }
          }
          if (!micIdIsMatch) {
            micId = recommend?.deviceId
            window.sessionStorage.removeItem('micId')
          }
        }
      } else {
        micId = recommend?.deviceId
      }
    } catch (err) {
      console.warn('checkMicDeviceInList---------', err)
    }

    return micId || ''
  }

  static checkCameraDeviceInList = async () => {
    let cameraId = window.sessionStorage.getItem('cameraId') || ''
    try {
      const cameras = await WebRTC2.getCameras()
      const recommend = cameras?.find((n: any) => !n.label?.match(/virtual|虚拟/i)) || cameras?.[0]
      if (cameraId) {
        if (cameras) {
          let cameraIdIsMatch = false
          for (let i = 0; i < cameras.length; i++) {
            if (cameras?.[i]?.deviceId === cameraId) {
              cameraIdIsMatch = true
              break
            }
          }
          if (!cameraIdIsMatch) {
            cameraId = recommend?.deviceId
            window.sessionStorage.removeItem('cameraId')
          }
        }
      } else {
        cameraId = recommend?.deviceId
      }
    } catch (err) {
      console.warn('checkCameraDeviceInList---------', err)
    }

    return cameraId || ''
  }

  @action
  setClient(client: any) {
    this.client = client
  }
  @action
  setDisconnected(state: boolean) {
    this.disconnected = state
  }
  @action setCurState(data: string) {
    this.curState = data
  }
  @action
  setReconnected(state: boolean) {
    this.reconnected = state
  }
  @action
  setConnecting(state: boolean) {
    this.connecting = state
  }
  @action
  setFirstConnecting(state: boolean) {
    this.firstConnecting = state
  }

  @action
  setAudioIsPlaying(state: boolean) {
    this.audioIsPlaying = state
  }

  @action
  async getDeviceOptions(resh: boolean) {
    if (resh) {
      if (window.isWeb) {
        refreshDeviceList()
      } else {
        refreshDeviceList({ audio: true })
      }
    }
    try {
      const device = await WebRTC2?.getDevices()
      if (device) {
        console.log('device-->: ', device)
        // 处理麦克风列表
        this.deviceOptions.audioIn = device.audioIn.map((item: any) => {
          return {
            label: item.label,
            value: item.deviceId,
          }
        })
        // 如果没有选择麦克风，则设置默认麦克风
        !this.audioInValue &&
          (this.audioInValue = device.audioIn.length > 0 ? device.audioIn[0].deviceId : '')

        // 处理扬声器列表
        this.deviceOptions.audioOut = device.audioOut.map((item: any) => {
          return {
            label: item.label,
            value: item.deviceId,
          }
        })
        // 如果没有选择扬声器，则设置默认扬声器
        // !this.audioOutValue && (this.audioOutValue = device.audioOut.length > 0 ? device.audioOut[0].deviceId : '');

        if (!this.audioOutValue && device.audioOut.length > 0) {
          const headphone = device.audioOut.find(
            // 优先选择耳机
            (item: any) =>
              item.label.toLowerCase().includes('bluetooth') ||
              item.label.toLowerCase().includes('headphone'),
          )

          // 优先选择耳机，如没有则选第一个设备
          this.audioOutValue = headphone ? headphone.deviceId : device.audioOut[0].deviceId
        }

        // 处理摄像头列表
        this.deviceOptions.videoList = device.video.map((item: any) => {
          return {
            label: item.label,
            value: item.deviceId,
          }
        })
        !this.videoValue &&
          (this.videoValue = device.video.length > 0 ? device.video[0].deviceId : '')
      }
    } catch (e) {
      console.error(e)
    }
  }

  @action
  setAudioOutValue(value: string, device?: deviceOption) {
    this.audioOutValue = value
    if (models.meeting?.speakerYxUid) {
      const stream = this.remoteStreamsMap.get(Number(models.meeting.speakerYxUid))
      stream && stream.setAudioOutput(value)
      this.audioOutValue = value
      if (device) {
        console.log(device)
      }
    }
  }

  @action
  async setAudioOutValueNext() {
    await this.getDeviceOptions(false)
    const list = this.deviceOptions.audioOut
    if (!list.length) {
      console.log('没有外放设备')
    } else {
      const presentIndex = list.findIndex(n => n.value === this.audioOutValue)
      if (presentIndex === -1) {
        await this.setAudioOutValue(list[0]?.value, list[0])
      } else {
        const next = list[(presentIndex + 1) % list.length]
        await this.setAudioOutValue(next?.value, next)
      }
    }
  }

  @action
  async setAudioInValue(value: string, device?: deviceOption) {
    if (this.localStream) {
      await this.localStream.switchDevice('audio', value)
      window.sessionStorage.setItem('micId', value)
      this.audioInValue = value
      if (device) {
        console.log(device)
      }
    }
  }

  @action
  async setAudioInValueNext() {
    await this.getDeviceOptions(false)
    const list = this.deviceOptions.audioIn
    if (!list.length) {
      console.log('没有麦克风设备')
    } else {
      const presentIndex = list.findIndex(n => n.value === this.audioInValue)
      if (presentIndex === -1) {
        await this.setAudioInValue(list[0]?.value, list[0])
      } else {
        const next = list[(presentIndex + 1) % list.length]
        await this.setAudioInValue(next?.value, next)
      }
    }
  }

  @action setisBackCamera(value: boolean) {
    this.isBackCamera = value
  }

  @action
  async setVideoValue(value: string) {
    if (this.localStream) {
      this.videoValue = value
      if (this.localStream?._play?.videoView) {
        this.localStream.videoView = this.localStream._play.videoView
      }
      await this.localStream.switchDevice('video', value).then(() => {
        this.localCameraId = value
        window.sessionStorage.setItem('cameraId', value)
      })
    }
  }

  @action
  async setVideoValueNext() {
    await this.getDeviceOptions(false)
    const videoLength = this.deviceOptions.videoList.length
    if (!this.deviceOptions.videoList.length) {
      console.log('没有摄像头')
      await this.closeCamera()
    } else {
      const presentIndex = this.deviceOptions.videoList.findIndex(n => n.value === this.videoValue)
      if (presentIndex === -1) {
        await this.setVideoValue(this.deviceOptions.videoList[0]?.value)
      } else {
        await this.setVideoValue(
          this.deviceOptions.videoList[(presentIndex + 1) % videoLength]?.value,
        )
      }
    }
  }

  @action
  async setVideoValueBack(back?: boolean) {
    let isBack = false
    if (typeof back === 'undefined') {
      isBack = !this.isBackCamera
    }
    await this.getDeviceOptions(false)
    if (!this.deviceOptions.videoList.length) {
      await this.closeCamera()
    } else {
      await this.closeCamera()
      this.openCamera(true, '', isBack)
    }
  }

  @action
  setAudioPlayErr(state: boolean) {
    this.audioPlayErr = state
  }

  @action
  async createLocalStream() {
    const isOpenVideo = JSON.parse(window.sessionStorage.getItem('isOpenVideo') || 'false')
    const isOpenAudio = JSON.parse(window.sessionStorage.getItem('isOpenAudio') || 'false')
    // this.isShareScreen = JSON.parse(window.sessionStorage.getItem('isShareScreen') || 'false');
    const micId = await RTCInstance.checkMicDeviceInList()
    const cameraId = await RTCInstance.checkCameraDeviceInList()
    const createStreamData: any = {
      uid: this.randomUid, // 本端的uid
      audio: micId && isOpenAudio, // 是否从麦克风采集音频
      video: cameraId && isOpenVideo, // 是否从摄像头采集视频
      screen: this.isShareScreen,
      highOrLow: 1,
    }
    if (micId) {
      createStreamData.microphoneId = micId
      this.audioInValue = micId
    }
    if (cameraId) {
      createStreamData.cameraId = cameraId
      this.videoValue = cameraId
    }
    if (this.client) {
      createStreamData.client = this.client
    }
    try {
      const localStream = await WebRTC2.createStream(createStreamData)
      try {
        localStream.setVideoEncoderConfiguration({
          mediaType: 'video',
          streamType: 'high',
          maxBitrate: 3_000_000,
          contentHint: 'motion',
        })
      } catch (e) {
        console.warn(e, '设置适配流，帧率优先失败')
      }
      WebRTC2.getParameters().allowEmptyMedia = true
      localStream.setAudioProfile('music_standard')
      // 设置视频属性
      localStream.setVideoProfile({
        resolution: WebRTC2.VIDEO_QUALITY_720p,
        frameRate: WebRTC2.CHAT_VIDEO_FRAME_RATE_15,
      })
      await localStream.init()
      // TODO： delete
      // localStream.mediaHelper.isLocal = true;
      ;(window as any).rtcClient = this.client
      // 设置本地音量，减少 啸叫 的可能性
      this.localStream = localStream
      this.localStream?.setAudioVolume(50)
      this.isOpenVideo = cameraId && isOpenVideo
      this.isOpenAudio = micId && isOpenAudio
      this.isShareScreen && api.screenShare(this.cid, true)
    } catch (err) {
      elkReport({ type: 'rtc创建本地流报错', logData: err })
    }
  }

  @action
  async setIsShareScreen(state: boolean) {
    if (state) {
      elkLog('打开屏幕共享')
      await this.openScreen(true)
    } else {
      await this.closeScreen()
    }
  }

  @computed
  get ableDoubleScreem() {
    // @ts-ignore
    const able = meeting.sortMembers.every(
      n =>
        !avleVersions[n.deviceType] ||
        (n.appVersion && compareVersions(n.appVersion, avleVersions[n.deviceType]) > -1),
    )
    return able
  }

  @action
  async openCamera(isPublish: boolean, deviceId?: string, isBack?: boolean) {
    const camera = await WebRTC2.getCameras()
    this.cameraIsNotAvailable = camera.length === 0
    if (this.cameraIsNotAvailable) return
    const cameraId = await RTCInstance.checkCameraDeviceInList()
    try {
      // 有关于 mediaHelper 的用法，需要联系云信 sdk 的开发人员，属于内部接口，并没有向外暴露
      // 如果已经发布流，则使用 open/close 控制设备状态，未发布时，使用 mediaHelper 控制
      if (this.isPublish) {
        const openData = {
          type: 'video',
          deviceId,
          facingMode: '',
        }
        if (!deviceId) {
          delete openData.deviceId
        }
        if (typeof isBack === 'boolean') {
          openData.facingMode = isBack ? 'environment' : 'user' // "user"：前置摄像头 , "environment"：后置摄像头
        } else if (cameraId && !deviceId) {
          openData.deviceId = cameraId
        }

        await this.localStream.open(openData)
        elkLog('打开摄像头成功')
        console.log('打开摄像头成功')
      } else if (this.localStream) {
        const data = {
          video: true,
          videoDeviceId: deviceId,
        }
        if (!deviceId) {
          delete data.videoDeviceId
        }
        if (cameraId && !deviceId) {
          data.videoDeviceId = cameraId
        }
        this.localStream.video = true
        elkLog('使用mediaHelper打开摄像头成功')
        await this.localStream.mediaHelper.getStream(data)
      }
      typeof isBack === 'boolean' && this.setisBackCamera(isBack)
      this.isOpenVideo = true
      window.sessionStorage.setItem('isOpenVideo', String(this.isOpenVideo))
      if (isPublish) {
        await this.publish()
      }
    } catch (err) {
      await this.closeCamera()
      console.log('用户不允许开启摄像头：', err)
      elkLog('用户不允许开启摄像头：err', {}, { err })
      dealDiviceErr(err)
    }
  }

  @action
  async openMic(isPublish: boolean, deviceId?: string) {
    const mic = await WebRTC2.getMicrophones()
    this.micIsNotAvailable = mic.length === 0
    if (this.micIsNotAvailable) return
    const micId = await RTCInstance.checkMicDeviceInList()
    try {
      // 兼容ios16设备导致音频崩溃
      if (
        this.localStream.hasAudio() &&
        satisfiesBrowser({ browser: 'safari', baseVersion: '^16.0.0' })
      ) {
        this.localStream.unmuteAudio()
      } else if (this.isPublish) {
        const openData = {
          type: 'audio',
          deviceId,
        }
        if (!deviceId) {
          delete openData.deviceId
        }
        if (micId && !deviceId) {
          openData.deviceId = micId
        }
        await this.localStream.open(openData)
        elkLog('打开麦克风成功')
      } else if (this.localStream) {
        const data = {
          audio: true,
          audioDeviceId: deviceId,
        }
        if (!deviceId) {
          delete data.audioDeviceId
        }
        if (micId && !deviceId) {
          data.audioDeviceId = micId
        }
        this.localStream.audio = true
        // TODO: 有关于 mediaHelper 的用法，需要联系云信 sdk 的开发人员，属于内部接口，并没有向外暴露
        // 如果已经发布流，则使用 open/close 控制设备状态，未发布时，使用 mediaHelper 控制
        elkLog('使用mediaHelper打开麦克风成功')
        await this.localStream.mediaHelper.getStream(data)
      }
      this.isOpenAudio = true
      window.sessionStorage.setItem('isOpenAudio', String(this.isOpenAudio))
      // 是否开启设备后后立即推流
      if (isPublish) {
        await this.publish()
      }
    } catch (err) {
      await this.closeMic()
      console.log('用户不允许开启麦克风：', err)
      elkLog('打开麦克风失败')
      dealDiviceErr(err)
    }
  }

  @action
  async openScreen(isPublish: boolean) {
    try {
      // 有关于 mediaHelper 的用法，需要联系云信 sdk 的开发人员，属于内部接口，并没有向外暴露
      // 如果已经发布流，则使用 open/close 控制设备状态，未发布时，使用 mediaHelper 控制
      if (this.isOpenVideo && !this.ableDoubleScreem) {
        await this.closeCamera()
      }
      this.localStream.setScreenProfile({ resolution: WebRTC2.VIDEO_QUALITY.VIDEO_QUALITY_1080p })
      let openRes
      if (this.isPublish) {
        openRes = await this.localStream.open({ type: 'screen' })
        elkLog('开启屏幕共享，并且推流到房间中')
      } else {
        elkLog('仅仅打开屏幕共享，不推流到房间中')
        openRes = await this.localStream.open({ type: 'screen', enableMediaPub: false })
      }
      if (isPublish) {
        elkLog('isPublish开启屏幕共享')
        await api.screenShare(this.cid, true)
      }
      runInAction(() => {
        this.localStream.screen = true
        window.sessionStorage.setItem('isShareScreen', String(this.isShareScreen))
        this.isShareScreen = true
      })
      if (isPublish) {
        await this.publish()
        meeting.setScreenUid(user.mid)
      }
    } catch (err) {
      if (String(err).indexOf('Permission denied by system') !== -1 && deviceData.os === 'Mac OS') {
        throw new Error('mac os system')
      } else {
        throw err
      }
    }
  }

  @action
  async closeCamera() {
    // 如果已经发布流，则使用 open/close 控制设备状态，未发布时，使用 mediaHelper 控制
    if (this.localStream) {
      if (this.isPublish) {
        this.localStream.close({ type: 'video' })
      } else {
        this.localStream.video = false
        await this.localStream.mediaHelper.stopStream('video')
        await this.localStream.stop('video')
      }
    }
    this.isOpenVideo = false
    window.sessionStorage.setItem('isOpenVideo', String(this.isOpenVideo))
  }

  @action
  async closeScreen(local = false) {
    if (this.localStream) {
      if (this.isPublish) {
        await this.localStream.close({ type: 'screen' })
        elkLog('关闭屏幕共享')
        this.localStream.screen = false
      } else {
        elkLog('使用mediaHelper关闭屏幕共享')
        await this.localStream.mediaHelper.stopStream('screen')
        this.localStream.screen = false
      }
      if (!local) {
        api.screenShare(this.cid, false)
        elkLog('api关闭屏幕共享')
        this.isShareScreen = false
        window.sessionStorage.setItem('isShareScreen', String(this.isShareScreen))
      }
      // 无论是否为本地关闭，都需要更新本地状态以保持UI同步
      runInAction(() => {
        this.isShareScreen = false
        window.sessionStorage.setItem('isShareScreen', String(this.isShareScreen))
      })
      // 仅在关闭本端屏幕共享时清空 screenUid，避免覆盖他人的共享状态
      if (meeting.screenUid === user.mid) {
        meeting.setScreenUid('')
      }
    }
  }

  @action
  async closeMic() {
    if (this.localStream) {
      // 兼容ios16设备导致音频崩溃
      if (
        this.localStream.hasAudio() &&
        satisfiesBrowser({ browser: 'safari', baseVersion: '^16.0.0' })
      ) {
        this.localStream.muteAudio()
      } else if (this.isPublish) {
        this.localStream.close({ type: 'audio' })
        elkLog('关闭麦克风', { meetingId: window.$meetingId, userId: user?.yxuid }, {})
      } else {
        // TODO: 如果已经发布流，则使用 open/close 控制设备状态，未发布时，使用 mediaHelper 控制
        this.localStream.audio = false
        await this.localStream.mediaHelper.stopStream('audio')
        elkLog('使用mediaHelper关闭麦克风')
      }
      this.isOpenAudio = false
      window.sessionStorage.setItem('isOpenAudio', String(this.isOpenAudio))
    }
  }

  @action
  async playLocalVideoStream(element: any) {
    if (this.localStream?.play && element) {
      await this.localStream?.play(element, { video: true })
    }
  }

  @action
  async checkDeviceStatus() {
    const camera = await WebRTC2.getCameras()
    const mic = await WebRTC2.getMicrophones()
    this.cameraIsNotAvailable = camera.length === 0
    this.micIsNotAvailable = mic.length === 0
  }

  @action
  async enableDualStream() {
    console.log('调用enableDualStream')
    this.client.enableDualStream({ video: true })
  }

  @action
  async join(yxUid: string, joinChannel: () => Promise<void>) {
    // 加入频道
    if (this.client && !this.joined) {
      const numberYxuid = Number.isNaN(Number(yxUid)) ? yxUid : Number(yxUid)
      this.localStream && (this.localStream.streamID = numberYxuid)
      this.localStream && this.localStream._play && (this.localStream._play.uid = numberYxuid)
      const item = this.client?.adapterRef?.mediaHelpers?.[this.randomUid]
      if (item) {
        this.client.adapterRef.mediaHelpers[yxUid] = item
        delete this.client.adapterRef.mediaHelpers[this.randomUid]
      }
      if (ua.os.name === 'iOS' && ua.os.version && compareVersions(ua.os.version, '26.2') >= 0) {
        WebRTC2.getParameters().enableCompatibleWithIOS26 = true
      }
      if (window.isMobile || window.isPad) {
        this.client.setCodecType('VP8')
      }
      await joinChannel()

      try {
        console.warn(
          this.client.adapterRef.mediaCapability.room.videoCodecType,
          '编码结果---------------------------------------',
        )
      } catch (err) {
        console.log(err, '编码结果报错---------------------------------------')
      }
      const meetingData = this.client.getChannelInfo()

      if (meetingData?.cid) {
        this.cid = meetingData?.cid || 0
        window.sessionStorage.setItem('cid', String(this.cid))
        setSentryUser({
          cid: String(this.cid),
        })
      } else {
        throw Error('join_error')
      }
      await this.publish()
      this.joined = true
    }
  }

  @action
  async publish() {
    // 将本地音视频流发布至云信服务器，加入房间前不用执行此方法。
    if (this.isOpenAudio || this.isOpenVideo || this.isShareScreen) {
      await this.client.publish(this.localStream)
      elkLog('成功发布本地流')
      this.isPublish = true
    }
  }

  // 通过云信id订阅远程视频流；
  @action
  async subscribeVideoStreamsByYxuid(yxuid: string | number, sub: boolean) {
    const map = new Map(this.remoteStreamsMap)
    const stream = map.get(Number(yxuid))
    if (!stream) return
    try {
      if (sub) {
        unSubTimerMap[yxuid] && clearTimeout(unSubTimerMap[yxuid])
        if (!stream?.pubStatus?.video?.consumerId) {
          stream.setSubscribeConfig({ video: true })
          await this.client.subscribe(stream)
          yxuid && this.updateRemoteStreams(stream, Number(yxuid), 'video')
          meeting?.updateMemberListByVideo() // 订阅完视屏流影响首页摄像头排序顺序 // 多次打开关闭摄像头也会调用
        }
      } else if (stream?.pubStatus?.video?.consumerId) {
        unSubTimerMap[yxuid] && clearTimeout(unSubTimerMap[yxuid])
        unSubTimerMap[yxuid] = setTimeout(async () => {
          stream.setSubscribeConfig({ video: false })
          await this.client.subscribe(stream)
          yxuid && this.updateRemoteStreams(stream, Number(yxuid), 'video')
        }, 2000)
      }
    } catch (err) {
      console.log('本地 subscribe 失败: ', err)
    }
  }

  @action
  async updateRemoteStreams(stream: any, yxuid: number, streamType?: string) {
    const map = new Map(this.remoteStreamsMap)
    if (map.get(Number(yxuid)) === stream) {
      if (['screen', 'video'].includes(streamType || '')) map.get(Number(yxuid)).update += 1
    } else {
      stream.update = 1
      map.set(Number(yxuid), stream)
    }
    this.remoteStreamsMap = map
  }

  @action
  async removeRemoteStreams(yxuid: number) {
    const map = new Map(this.remoteStreamsMap)
    map.delete(yxuid)
    this.remoteStreamsMap = map
  }

  @action
  muteAudio(yxuid: number) {
    this.muteMap.set(yxuid.toString(), true)
  }

  @action
  unmuteAudio(yxuid: number) {
    this.muteMap.set(yxuid.toString(), false)
  }

  @action
  async getLocalVideoStats(
    boxWidth: number,
    boxHeight: number,
    originWidth?: number,
    originHeight?: number,
  ) {
    let width = boxWidth
    let height = boxHeight
    let ow = originWidth || boxWidth
    let oh = originHeight || boxHeight
    if (this.client) {
      const res = await this.client.getLocalVideoStats()
      if (res && res.length > 0) {
        ow = res[0].SendResolutionWidth
        oh = res[0].SendResolutionHeight
      }
      const proportion = ow / oh
      if (proportion) {
        height = width / proportion
        if (height > boxHeight) {
          height = boxHeight
          width = height * proportion
        }
      }
    }
    // 4.6.40版本 限制了宽高必须是整数 后续待定
    return {
      width: Math.floor(width),
      height: Math.floor(height),
    }
  }

  @action
  async getRemoteVideoStats(
    boxWidth: number,
    boxHeight: number,
    yxuid: string,
    originWidth?: number,
    originHeight?: number,
  ) {
    const res = await this.client.getRemoteVideoStats()
    let width = boxWidth
    let height = boxHeight
    let ow = originWidth || boxWidth
    let oh = originHeight || boxHeight

    if (res && res[yxuid]) {
      res[yxuid].RecvResolutionWidth && (ow = res[yxuid].RecvResolutionWidth)
      res[yxuid].RecvResolutionHeight && (oh = res[yxuid].RecvResolutionHeight)
    }
    const proportion = ow / oh
    if (proportion && proportion !== 16 / 9) {
      height = width / proportion
      if (height > boxHeight) {
        height = boxHeight
        width = height * proportion
      }
    }
    // 4.6.40版本 限制了宽高必须是整数 后续待定
    return {
      width: Math.floor(width),
      height: Math.floor(height),
    }
  }

  @action
  calcVideoSize(boxWidth: number, boxHeight: number, originWidth?: number, originHeight?: number) {
    let width = boxWidth
    let height = boxHeight
    const ow = originWidth || boxWidth
    const oh = originHeight || boxHeight
    const proportion = ow / oh
    if (proportion) {
      height = width / proportion
      if (height > boxHeight) {
        height = boxHeight
        width = height * proportion
      }
    }
    // 4.6.40版本 限制了宽高必须是整数 后续待定
    return {
      width: Math.floor(width),
      height: Math.floor(height),
    }
  }

  @action
  async destroy() {
    if (this.client && this.localStream) {
      this.joined = false
      this.localStream.destroy()
      await this.client.leave()
      this.client.destroy()
    }
  }

  @computed
  get breakLineReconnecting() {
    if (this.connecting && !this.firstConnecting) {
      return true
    }
    return false
  }

  /**
   * 获取一个用户的音频视频的状态
   */
  getMemberInfo(useryxUid: number, yxuid: number) {
    const isMySelf = useryxUid === yxuid
    if (isMySelf) {
      return {
        isOpenAudio: this.isOpenAudio,
        isOpenVideo: this.isOpenVideo,
        isShareScreen: this.isShareScreen,
        isMySelf,
        stream: this.localStream,
        isBackCamera: this.isBackCamera,
      }
    }
    // 音视频播放状态目前是通过流返回控制,对象直接存储是一个map, 云信id和对应的流.
    // TODO 建议将这种模式改成和人员信息走, 流信息仅维护当前频道的流
    // 现在同声传译在切换到其他语言频道,老代码把存储的对象中的数据给清空了 但是按照策划需求其他语言频道说话也要控制当前这个人的话筒状态
    // 传译其他频道不受主持人静音操作的控制
    const remoteStream = this.remoteStreamsMap.get(Number(yxuid))
    return {
      isOpenAudio: !!remoteStream?.audio && this.muteMap.get(yxuid.toString()),
      isOpenVideo: !!remoteStream?.video,
      isShareScreen: !!remoteStream?.screen,
      isMySelf: false,
      stream: remoteStream,
      isBackCamera: false,
    }
  }
}

export default RTCInstance
